/**
 * ProjectTree Component
 * Displays hierarchical project list with creation form
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { FolderOpen, Folder, FileText, Plus } from 'lucide-react';
import { API, getApiUrl } from '../../utils/api';

const ProjectTree = ({ projects, currentProject, onProjectSelect, onCreateProject }) => {
  const { t } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectParent, setNewProjectParent] = useState(null);

  const handleCreateProject = async (parentId = null) => {
    if (!newProjectName.trim()) {
      console.log('Empty project name, not creating project');
      return;
    }
    
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      console.log('Creating project with data:', {
        name: newProjectName,
        parent_id: parentId,
        project_type: parentId ? 'sub' : 'main',
        creator_id: user.id
      });
      
      const response = await axios.post(`${getApiUrl()}/projects?creator_id=${user.id}`, {
        name: newProjectName,
        parent_id: parentId,
        project_type: parentId ? 'sub' : 'main'
      });
      
      console.log('Project creation response:', response.data);
      
      setNewProjectName('');
      setNewProjectParent(null);
      setShowCreateForm(false);
      
      // Notify parent to reload projects
      onCreateProject(response.data);
      
    } catch (error) {
      console.error('Project creation error:', error);
      alert(t('common.errorCreating', 'Error creating: ') + (error.response?.data?.detail || error.message));
    }
  };

  const renderProjects = (projectList, level = 0) => {
    return projectList.map(project => (
      <div key={project.id}>
        <div
          className={`flex items-center p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 ${
            currentProject?.id === project.id ? 'bg-emerald-100 border-l-4 border-emerald-500' : ''
          }`}
          style={{ marginLeft: `${level * 16}px` }}
          onClick={() => onProjectSelect(project)}
        >
          {/* Visual hierarchy indicator */}
          {level > 0 && (
            <>
              <div className="flex items-center mr-2">
                {Array.from({ length: level }).map((_, i) => (
                  <div key={i} className="w-4 flex justify-center">
                    {i === level - 1 ? (
                      <div className="w-3 h-3 border-l-2 border-b-2 border-gray-300 mb-1.5"></div>
                    ) : (
                      <div className="w-px h-6 bg-gray-300"></div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* Project icon based on level */}
          {level === 0 ? (
            <FolderOpen className="w-4 h-4 text-emerald-600 mr-2" />
          ) : level === 1 ? (
            <Folder className="w-3.5 h-3.5 text-emerald-500 mr-2" />
          ) : (
            <FileText className="w-3.5 h-3.5 text-emerald-400 mr-2" />
          )}
          
          <span className={`flex-1 font-medium text-sm ${
            level === 0 ? 'text-gray-800' : 
            level === 1 ? 'text-gray-700' : 
            'text-gray-600'
          }`}>
            {project.name}
          </span>
          
          <Badge 
            variant="secondary" 
            className={`text-xs px-1.5 py-0 ${
              level === 0 ? 'bg-emerald-100 text-emerald-700' :
              level === 1 ? 'bg-blue-100 text-blue-700' :
              'bg-purple-100 text-purple-700'
            }`}
          >
            {level === 0 ? 'main' : `sub-${level}`}
          </Badge>
        </div>
        
        {/* Render children */}
        {project.children && project.children.length > 0 && renderProjects(project.children, level + 1)}
      </div>
    ));
  };

  return (
    <div className="h-full flex flex-col" style={{ height: '100%', maxHeight: '100vh' }}>
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-800">{t('projects.title', 'Projects')}</h3>
          <Button
            size="sm"
            onClick={() => setShowCreateForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            {t('common.new', 'New')}
          </Button>
        </div>
        
        {showCreateForm && (
          <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('projects.projectName', 'Project name')}
              </label>
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t('projects.namePlaceholder', 'e.g. Building 1, Kitchen, Heating room...')}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('projects.projectType', 'Project type')}
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={newProjectParent ? "outline" : "default"}
                  size="sm"
                  onClick={() => setNewProjectParent(null)}
                  className="flex-1 min-w-[120px]"
                >
                  🏠 {t('projects.mainProject', 'Main project')}
                </Button>
                <Button
                  variant={newProjectParent ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (newProjectParent) {
                      setNewProjectParent(null);
                    } else {
                      const parentId = currentProject?.id || projects.find(p => p.project_type === 'main')?.id;
                      setNewProjectParent(parentId || 'default');
                    }
                  }}
                  className="flex-1 min-w-[120px]"
                >
                  📁 {t('projects.subProject', 'Subproject')}
                </Button>
              </div>
              
              {/* Parent Project Selection */}
              {newProjectParent && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('projects.parentProject', 'Parent project')}
                  </label>
                  <select
                    value={newProjectParent}
                    onChange={(e) => setNewProjectParent(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">-- {t('projects.selectMainProject', 'Select main project')} --</option>
                    {projects.filter(p => p.project_type === 'main').map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {newProjectParent && (
              <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                ℹ️ {t('projects.creatingAsSubOf', 'Will be created as subproject of "{{name}}"').replace('{{name}}', currentProject?.name)}
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => handleCreateProject(newProjectParent)}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!newProjectName.trim()}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('common.create', 'Create')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewProjectName('');
                  setNewProjectParent(null);
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <div 
        className="flex-1 p-2"
        style={{ 
          flex: '1 1 0%',
          minHeight: 0,
          maxHeight: 'calc(100% - 120px)',
          overflowY: 'scroll',
          overflowX: 'hidden',
          height: 'auto'
        }}
      >
        {renderProjects(projects)}
      </div>
    </div>
  );
};

export default ProjectTree;
